import { z } from "zod";
export const SupportStatusSchema = z.enum([
  "OPEN",
  "IN_PROGRESS",
  "RESOLVED",
  "CLOSED",
]);
export const SupportPrioritySchema = z.enum([
  "LOW",
  "MEDIUM",
  "HIGH",
  "CRITICAL",
]);
export const SupportCategorySchema = z.enum([
  "TECHNICAL",
  "BILLING",
  "FEATURE",
  "TAX",
]);
const text = (min: number, max: number) =>
  z
    .string()
    .trim()
    .min(min)
    .max(max)
    .refine(
      (value) => new TextEncoder().encode(value).byteLength <= max,
      `Text must not exceed ${max} bytes`,
    );
export const SupportTicketCreateSchema = z
  .object({
    subject: text(5, 200),
    category: SupportCategorySchema,
    description: text(20, 20000),
    priority: SupportPrioritySchema,
  })
  .strict();
export const SupportTicketReplySchema = z
  .object({ message: text(1, 20000) })
  .strict();
export const SupportAdminReplySchema = z
  .object({
    message: text(1, 20000).optional(),
    status: SupportStatusSchema.optional(),
  })
  .strict()
  .refine(
    (value) => value.message !== undefined || value.status !== undefined,
    "A reply or status is required",
  );
export const SupportListQuerySchema = z
  .object({
    status: SupportStatusSchema.optional(),
    priority: SupportPrioritySchema.optional(),
    search: z.string().trim().max(200).optional(),
    limit: z.number().int().min(1).max(100).default(50),
    offset: z.number().int().min(0).max(100000).default(0),
  })
  .strict();
export const SupportRepliesQuerySchema = SupportListQuerySchema.pick({
  limit: true,
  offset: true,
}).extend({ limit: z.number().int().min(1).max(100).default(100) });
const id = z.string().min(1).max(256),
  time = z.string().min(1).max(64);
export const SupportTicketSchema = z.object({
  id,
  tenant_id: id,
  user_id: id,
  ticket_number: z.string().min(1).max(128),
  subject: z.string(),
  description: z.string(),
  category: SupportCategorySchema,
  priority: SupportPrioritySchema,
  status: SupportStatusSchema,
  created_at: time,
  updated_at: time,
  user_email: z.string().nullish(),
  tenant_name: z.string().nullish(),
});
export const SupportReplySchema = z.object({
  id,
  ticket_id: id,
  user_id: id,
  message: z.string(),
  created_at: time,
});
export const SupportCountsSchema = z.object({
  OPEN: z.number().int().nonnegative(),
  IN_PROGRESS: z.number().int().nonnegative(),
  RESOLVED: z.number().int().nonnegative(),
  CLOSED: z.number().int().nonnegative(),
});
const page = {
  total: z.number().int().nonnegative(),
  pagination: z.object({
    limit: z.number().int().min(1).max(100),
    offset: z.number().int().nonnegative(),
  }),
};
export const SupportTicketPageSchema = z.object({
  data: z.array(SupportTicketSchema).max(100),
  ...page,
  counts: SupportCountsSchema,
});
export const SupportTicketResponseSchema = z.object({
  data: SupportTicketSchema,
});
export const SupportRepliesPageSchema = z.object({
  data: z.array(SupportReplySchema).max(100),
  ...page,
});
export const SupportCreateResultSchema = z.object({
  status: z.literal("success"),
  id,
  ticketNumber: z.string().min(1).max(128),
});
export const SupportMutationResultSchema = z.object({
  success: z.literal(true),
});
export type SupportListQuery = z.infer<typeof SupportListQuerySchema>;
export type SupportTicket = z.infer<typeof SupportTicketSchema>;
export type SupportReply = z.infer<typeof SupportReplySchema>;
